import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import { ConsoleShell, PageHead } from "../components/ConsoleShell.js";
import { KpiCard, Tag, BtnLink } from "../components/index.js";
import { Card } from "../components/Card.js";
import { Frame } from "../components/Frame.js";
import { Banner } from "../components/Banner.js";
import { LoadingState, ErrorState, EmptyState } from "../components/EmptyState.js";
import { Icon, type IconName } from "../components/Icon.js";
import { BookCover } from "../components/BookCover.js";
import { coverModel } from "../components/Bookshelf.js";
import { useAsync } from "../lib/useAsync.js";
import { manuals, books as booksApi, stores as storesApi, getClaims, hasRole, bookTargetsViewer, mediaUrl } from "../lib/api.js";
import { isOrphanDraft } from "../lib/manualFilters.js";
import { listQueue, flushQueue, removeQueued, type QueueItem } from "../lib/offline.js";
import { hydrateProgress } from "../lib/readingProgress.js";
import type { Book, Manual, Store } from "@tebiki/shared";

const STATUS_LABEL: Record<string, string> = {
  draft: "下書き",
  processing: "AI処理中",
  review: "承認待ち",
  published: "公開済み",
  archived: "アーカイブ",
  failed: "失敗",
};

/** Quick-create modes (creators only — viewers get their 本棚 instead). */
const MODES: { to: string; icon: IconName; kicker: string; title: string; desc: string }[] = [
  { to: "/capture/video", icon: "mode-video", kicker: "RECOMMENDED", title: "動画から作成", desc: "声で実況しながら撮るだけ。" },
  { to: "/capture/image", icon: "mode-image", kicker: "STEP BY STEP", title: "画像から作成", desc: "手順ごとに撮影＋口頭説明。" },
  { to: "/capture/screen", icon: "mode-screen", kicker: "FOR PC", title: "画面録画から作成", desc: "PC画面＋マイクで実況。" },
];

const heroCardStyle: CSSProperties = {
  position: "relative",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  padding: "26px 28px",
  minHeight: 236,
};
const heroGhost: CSSProperties = {
  position: "absolute",
  right: 16,
  top: 4,
  lineHeight: 0.8,
  color: "color-mix(in srgb, var(--blueprint) 55%, transparent)",
  opacity: 0.8,
  pointerEvents: "none",
};

type DashData = [Manual[], Book[], Store[]];

/**
 * The post-login hub for EVERY role — content adapts:
 *  - creators (owner/admin/editor): capture quick-create + offline queue + 作業中
 *  - managers (owner/admin/reviewer): KPIs + 承認 nudge
 *  - everyone: 自分の本棚 (books targeting the viewer, 必読 first)
 */
export function Dashboard() {
  const canCreate = hasRole("owner", "admin", "editor");
  const canReview = hasRole("owner", "admin", "reviewer");
  const claims = getClaims();

  const { data, loading, error, offline, reload } = useAsync<DashData>(
    async () => {
      const [list, bookList, stores] = await Promise.all([
        manuals.list().then((r) => r.items.filter((m) => !isOrphanDraft(m.status, m.title))),
        booksApi.list().catch(() => [] as Book[]),
        storesApi.list().catch(() => [] as Store[]),
      ]);
      return [list, bookList, stores];
    },
  );
  const [items, books, stores] = data ?? [[], [], []];

  // Offline capture queue (ported from the retired 作成者ホーム — creators only).
  const [queue, setQueue] = useState<QueueItem[]>([]);
  useEffect(() => {
    if (!canCreate) return;
    listQueue().then(setQueue).catch(() => setQueue([]));
  }, [canCreate]);
  // 別デバイスで読んだ進捗を一度だけ取り込む（本棚リング・続きから・修了の同期）。
  useEffect(() => {
    void hydrateProgress();
  }, []);
  const waiting = queue.filter((qi) => qi.status !== "done").length;

  // 今すぐ送信 — honest feedback: busy state + per-result message (silent failure was
  // indistinguishable from "the button does nothing").
  const [flushing, setFlushing] = useState(false);
  const [flushMsg, setFlushMsg] = useState<string | null>(null);
  async function sendQueueNow() {
    if (flushing) return;
    setFlushing(true);
    setFlushMsg(null);
    const res = await flushQueue();
    setQueue(await listQueue().catch(() => [] as QueueItem[]));
    setFlushing(false);
    if (res.blocked === "offline") setFlushMsg("オフラインのため送信できません。接続が戻ると自動送信されます。");
    else if (res.blocked === "auth") setFlushMsg("ログインが必要です。ログインすると自動送信されます。");
    else if (res.failed > 0) setFlushMsg(`${res.sent}件を送信、${res.failed}件が失敗しました。「内容を確認」から個別に確認・再送信できます。`);
    else if (res.sent > 0) setFlushMsg(`${res.sent}件を送信しました。AI処理が始まります。`);
  }

  const counts = useMemo(
    () => ({
      total: items.length,
      published: items.filter((m) => m.status === "published").length,
      review: items.filter((m) => m.status === "review").length,
      processing: items.filter((m) => m.status === "processing").length,
    }),
    [items],
  );

  const manualById = useMemo(() => new Map(items.map((m) => [m.manualId, m])), [items]);
  const storeNameOf = (b: Book) =>
    b.audience.storeIds.length === 0
      ? "全店共通"
      : (stores.find((s) => s.id === b.audience.storeIds[0])?.name ?? "店舗") + (b.audience.storeIds.length > 1 ? " ほか" : "");

  /** Books meant for this viewer — 必読 first, then recency. */
  const myBooks = useMemo(() => {
    const targeted = books.filter((b) => bookTargetsViewer(b, claims));
    return targeted.sort((a, b) => Number(b.mustRead) - Number(a.mustRead) || b.updatedAt - a.updatedAt).slice(0, 8);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [books]);

  // 作業中 (creators): drafts / processing / 承認待ち — the "finish what you started" tray.
  const working = useMemo(
    () => items.filter((m) => m.status === "draft" || m.status === "processing" || m.status === "review").slice(0, 5),
    [items],
  );
  // 閲覧者: recent published to continue reading.
  const readable = useMemo(() => items.filter((m) => m.status === "published").slice(0, 5), [items]);
  const recent = canCreate ? working : readable;
  const org = claims?.org;

  return (
    <ConsoleShell
      breadcrumb={["TEBIKI", "ダッシュボード"]}
      actions={
        canCreate ? (
          <BtnLink to="/capture/video" state={{ from: "/dashboard" }} variant="primary" icon={<Icon name="capture" />}>
            撮影をはじめる
          </BtnLink>
        ) : undefined
      }
    >
      <PageHead title={<>今日の<span className="hl">ダッシュボード</span></>}>
        {canCreate ? <>撮って、束ねて、届ける。{org ? `組織「${org}」の` : ""}マニュアルの全体像と次の一手をここから。</> : <>あなたに割り当てられた本と、続きから読むマニュアル。</>}
      </PageHead>

      {loading ? (
        <LoadingState label="ダッシュボードを読み込み中…" />
      ) : error ? (
        <ErrorState message={error} offline={offline} onRetry={reload} />
      ) : (
        <>
          {canCreate && (waiting > 0 || flushMsg) ? (
            <Banner variant="warn" icon={<Icon name="offline" />} className="reveal">
              <span style={{ display: "block" }}>
                {waiting > 0 ? (
                  <>
                    オフラインで撮影した <b>{waiting}件</b> がアップロード待ちです。接続時に自動送信されます。
                  </>
                ) : (
                  <>アップロード待ちはありません。</>
                )}
                {flushMsg ? (
                  <span className="jp" role="status" style={{ display: "block", marginTop: 6, fontSize: 13 }}>
                    {flushMsg}
                  </span>
                ) : null}
              </span>
              {waiting > 0 ? (
                <span style={{ display: "inline-flex", gap: 8, marginLeft: 12, flexWrap: "wrap" }}>
                  <button className="tag tag--hi" type="button" style={{ border: 0 }} disabled={flushing} onClick={() => void sendQueueNow()}>
                    {flushing ? "送信中…" : "今すぐ送信"}
                  </button>
                  <Link className="tag" to="/inbox" style={{ textDecoration: "none" }}>
                    内容を確認
                  </Link>
                  <button
                    className="tag"
                    type="button"
                    disabled={flushing}
                    onClick={async () => {
                      if (!confirm("アップロード待ちの撮影データを破棄しますか？この操作は取り消せません。")) return;
                      await Promise.all(queue.filter((qi) => qi.status !== "done").map((qi) => removeQueued(qi.id)));
                      setQueue(await listQueue());
                    }}
                  >
                    破棄
                  </button>
                </span>
              ) : null}
            </Banner>
          ) : null}

          {/* ---- quick create (creators) ---- */}
          {canCreate ? (
            <div className="dash-hero">
              <Link className="card crop reveal" to="/capture/video" state={{ from: "/dashboard" }} style={heroCardStyle}>
                <span className="num num--outline" aria-hidden="true" style={heroGhost}>
                  <Icon name="mode-video" size={210} style={{ color: "var(--ink)" }} />
                </span>
                <div className="kicker" style={{ color: "var(--blueprint-ink)" }}>RECOMMENDED · 動画</div>
                <div style={{ marginTop: "auto" }}>
                  <div className="display" style={{ fontSize: "var(--step-3)", lineHeight: 1 }}>動画から作成</div>
                  <p className="jp" style={{ color: "var(--muted)", maxWidth: 340, margin: "8px 0 0", fontSize: 14 }}>
                    声で実況しながら撮るだけ。AIが手順に区切ります。
                  </p>
                </div>
                <span className="go" aria-hidden="true" style={{ position: "absolute", right: 22, bottom: 16, color: "var(--hi-deep)" }}>
                  <Icon name="arrow-right" size={16} />
                </span>
              </Link>
              <div style={{ display: "grid", gridTemplateRows: "1fr 1fr", gap: 16, minWidth: 0 }}>
                {MODES.slice(1).map((m, i) => (
                  <Link key={m.to} className={`card crop mode reveal d${i + 1}`} to={m.to} state={{ from: "/dashboard" }}>
                    <div className="big"><Icon name={m.icon} size={40} /></div>
                    <div style={{ minWidth: 0 }}>
                      <div className="kicker">{m.kicker}</div>
                      <div className="display" style={{ fontSize: "var(--step-2)", margin: "2px 0" }}>
                        {m.title}
                      </div>
                      <div className="jp" style={{ color: "var(--muted)", fontSize: 13 }}>
                        {m.desc}
                      </div>
                    </div>
                    <div className="go"><Icon name="arrow-right" size={16} /></div>
                  </Link>
                ))}
              </div>
            </div>
          ) : null}

          {/* ---- KPIs + 承認 nudge (managers) ---- */}
          {canReview ? (
            <>
              <div className="grid g-4" style={{ marginBottom: 18 }}>
                <KpiCard label="マニュアル総数" value={counts.total} className="reveal" />
                <KpiCard label="公開中" value={counts.published} className="reveal d1" />
                <KpiCard label="承認待ち" value={counts.review} suffix="件" className="reveal d2" />
                <KpiCard label="AI処理中" value={counts.processing} className="reveal d3" />
              </div>
              {counts.review > 0 ? (
                <Banner variant="ai" icon={<Icon name="approval" />} className="reveal">
                  <span className="jp grow">
                    <b>{counts.review}件</b> のマニュアルが提出され、承認待ちです。内容を確認して公開しましょう。
                  </span>
                  <BtnLink to="/approval" style={{ minHeight: 44, padding: "8px 14px" }}>
                    承認トレイへ
                  </BtnLink>
                </Banner>
              ) : null}
            </>
          ) : null}

          {/* ---- あなたの本棚 (every role) ---- */}
          <section aria-label="あなたの本棚" style={{ marginTop: 22 }}>
            <div className="spread" style={{ marginBottom: 10, alignItems: "baseline" }}>
              <h2 className="display" style={{ fontSize: 24 }}>あなたの<span className="hl">本棚</span></h2>
              <Link className="tag" to="/library">書架をすべて見る</Link>
            </div>
            {myBooks.length === 0 ? (
              <Card pad className="reveal">
                <div className="jp" style={{ color: "var(--muted)", fontSize: 14 }}>
                  {canCreate
                    ? "まだ本がありません。書架の「＋新しい本」でマニュアルを束ねると、対象スタッフの本棚に自動で並びます。"
                    : "あなた宛の本はまだありません。届くとここに並びます。"}
                </div>
              </Card>
            ) : (
              <div className="shelf">
                <div className="shelf-rail">
                  {myBooks.map((b) => (
                    <BookCover key={b.bookId} model={coverModel(b, manualById, storeNameOf(b))} to={`/book/${b.bookId}`} />
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* ---- 作業中 (creators) / 続きから (viewers) ---- */}
          <div className="grid g-2" style={{ marginTop: 22, alignItems: "start" }}>
            <Card crop className="reveal d1">
              <div className="spread" style={{ padding: "16px 18px 8px" }}>
                <div className="kicker">{canCreate ? "作業中のマニュアル" : "読めるマニュアル"}</div>
                <Link className="tag" to="/library">
                  すべて見る
                </Link>
              </div>
              {recent.length === 0 ? (
                <EmptyState icon={<Icon name="library" size={30} />} title={canCreate ? "作業中のものはありません" : "公開中のマニュアルがありません"}>
                  {canCreate ? "「撮影をはじめる」から、次の手順書を作りましょう。" : "公開されるとここに並びます。"}
                </EmptyState>
              ) : (
                recent.map((m, i) => (
                  <Link
                    key={m.manualId}
                    to={
                      m.status === "processing"
                        ? `/processing/${m.manualId}`
                        : canCreate
                          ? `/preview/${m.manualId}`
                          : `/read/${m.manualId}`
                    }
                    className="draft"
                    style={{
                      display: "grid",
                      gridTemplateColumns: "56px 1fr auto",
                      gap: 14,
                      alignItems: "center",
                      padding: "12px 16px",
                      borderBottom: i < recent.length - 1 ? "1px solid var(--line)" : undefined,
                    }}
                  >
                    <Frame paper className="thumb" style={{ width: 56, aspectRatio: "1" }} src={mediaUrl(m.thumbnailUrl)} subject={m.status !== "published"} />
                    <div style={{ minWidth: 0 }}>
                      <div className="display jp" style={{ fontSize: 16, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {m.title}
                      </div>
                      <div className="row" style={{ marginTop: 4, gap: 8 }}>
                        <Tag
                          dot
                          color={
                            m.status === "published"
                              ? "var(--ok)"
                              : m.status === "processing"
                                ? "var(--hi)"
                                : m.status === "review"
                                  ? "var(--info)"
                                  : "var(--muted)"
                          }
                        >
                          {STATUS_LABEL[m.status] ?? m.status}
                        </Tag>
                        <Tag mono>{m.sourceType === "video" ? "動画" : "画像"}</Tag>
                      </div>
                    </div>
                    <span className="go" aria-hidden="true" style={{ color: "var(--muted)" }}>
                      <Icon name="arrow-right" size={16} />
                    </span>
                  </Link>
                ))
              )}
            </Card>

            <Card crop pad className="reveal d2">
              <div className="kicker">ショートカット</div>
              <div className="stack" style={{ gap: 2, marginTop: 10 }}>
                {(
                  [
                    ["/library", "library", "書架（ライブラリ）"],
                    ...(canReview ? ([["/approval", "approval", "承認待ち"]] as [string, IconName, string][]) : []),
                    ...(canCreate ? ([["/multilingual", "multilingual", "多言語"]] as [string, IconName, string][]) : []),
                    ...(canReview ? ([["/analytics", "analytics", "分析"], ["/learning", "learning", "学習管理"]] as [string, IconName, string][]) : []),
                    ["/inbox", "inbox", "受信箱"],
                    ["/settings", "settings", "設定"],
                  ] as [string, IconName, string][]
                ).map(([to, icon, label]) => (
                  <Link key={to} className="navlink" to={to}>
                    <span className="ni" aria-hidden="true">
                      <Icon name={icon} size={16} />
                    </span>
                    <span>{label}</span>
                  </Link>
                ))}
              </div>
            </Card>
          </div>
        </>
      )}
    </ConsoleShell>
  );
}
